import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { showHideToggles } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import { useTrackChangesQuery } from 'components/WorkspaceEditor/components/TrackChangesPanel/api'
import {
    useContentMutationService,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import {
    useShallowSelector,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { debounce } from 'lodash'
import { TrackChangeType } from 'models/changes'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { PROPERTY_EDITOR_PILL_STATE_VARIANT } from '../constants'
import {
    updateElementById,
    updateElements,
    usePillSelectionSelector,
} from '../store/PillSelectionStore'

const DEBOUNCE_INTERVAL = 300

/**
 * Hook that encapsulates pill selection functionality for text content elements
 *
 * @returns An object containing state and functions for pill selection operations
 */
export const usePillSelection = () => {
    const {
        nodes,
        previousNodes,
        setContentItemViewNodes,
        setTextContentElementToAdd,
    } = useShallowSelector((state) => ({
        nodes: state.contentItemViewNodes,
        previousNodes: state.previousContentItemViewNodes,
        textContentElementtoAdd: state.textContentElementToAdd,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
        setTextContentElementToAdd: state.actions.setTextContentElementToAdd,
    }))

    const {
        isValidVariant,
        calculatePillStateChange,
        setPillSelection,
        resetPillSelectionState,
    } = usePillSelectionSelector((s) => ({
        isValidVariant: s.isValidVariant,
        calculatePillStateChange: s.calculatePillStateChange,
        setPillSelection: s.setPillSelection,
        resetPillSelectionState: s.resetPillSelectionState,
    }))

    const { executeWithOptimistic } = useTransactionManager()
    const { mutateContentElements } = useContentMutationService()
    const { fetchTrackChangesData } = useTrackChangesQuery()

    // Track component mount status
    const isMountedRef = useRef(true)

    useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])

    // Debounced fetch to prevent race conditions
    const debouncedFetchTrackChanges = useMemo(
        () =>
            debounce(async () => {
                if (!isMountedRef.current) return

                try {
                    await fetchTrackChangesData()
                } catch (error) {
                    if (!isMountedRef.current) return
                }
            }, DEBOUNCE_INTERVAL),
        [fetchTrackChangesData]
    )

    useEffect(() => {
        return () => {
            debouncedFetchTrackChanges.cancel()
        }
    }, [debouncedFetchTrackChanges])
    /**
     * Handles optimistic update for adding a new content item
     */
    const updateOptimistically = useCallback(
        (
            itemId: string,
            newElements: TextContentElement[],
            success = false,
            isPillUpdate = false,
            replacementOptionToFocus?: Partial<TextContentElement>
        ) => {
            // Get the current state from the store at execution time
            const currentNodes = useWorkspace2Store.getState()
                .contentItemViewNodes

            const updatedNodes = success
                ? updateElements(
                      itemId,
                      newElements,
                      currentNodes as ContentItemViewNodesType[],
                      isPillUpdate
                  )
                : previousNodes || []

            setContentItemViewNodes(updatedNodes)
            // If this is a ReplacementOption being depilled (OptedIn -> editable), mark it for auto-focus
            // Set immediately without requestAnimationFrame to reduce latency
            if (replacementOptionToFocus) {
                setTextContentElementToAdd(replacementOptionToFocus)
            }
        },
        [previousNodes, setContentItemViewNodes, setTextContentElementToAdd]
    )

    /**
     * Execute the mutation to update pill state with optimistic update
     */
    const activateDeactivateOptionItem = useCallback(
        (
            unsavedData: Partial<TextContentElement>,
            itemId: string,
            isReset?: boolean,
            replacementOptionToFocus?: Partial<TextContentElement>
        ) => {
            // Validate elements exist before proceeding
            if (!unsavedData.elements || unsavedData.elements.length === 0) {
                return
            }

            const elementsData = (isReset
                ? unsavedData.elements
                : [unsavedData]) as TextContentElement[]
            const elementId = isReset ? elementsData[0].id : itemId
            updateOptimistically(
                itemId,
                unsavedData.elements as TextContentElement[],
                true,
                true
            )

            executeWithOptimistic({
                fallbackState: nodes,
                asyncAction: async () => {
                    return await mutateContentElements(
                        elementId,
                        elementsData,
                        !!isReset
                    )
                },
                successCbFn: async () => {
                    if (replacementOptionToFocus) {
                        requestAnimationFrame(() => {
                            setTextContentElementToAdd(replacementOptionToFocus)
                        })
                    }
                    await debouncedFetchTrackChanges()
                },
                errorCbFn: () => {
                    if (!unsavedData.elements) return

                    updateOptimistically(
                        itemId,
                        unsavedData.elements as TextContentElement[]
                    )
                },
            })
        },
        [
            executeWithOptimistic,
            updateOptimistically,
            mutateContentElements,
            debouncedFetchTrackChanges,
            setTextContentElementToAdd,
            nodes,
        ]
    )

    /**
     * Register a pill selection with the store
     */
    const register = useCallback(
        (
            selectedPill: Partial<TextContentElement>,
            parentItem: Partial<TextContentElement>,
            isSelected: boolean = false,
            contentItemViewNodes: Partial<ContentItemViewNodesType>[] = [],
            multiType: string = ''
        ) => {
            setPillSelection({
                selectedPill,
                parentItem,
                isSelected,
                contentItemViewNodes,
                multiType,
            })
        },
        [setPillSelection]
    )

    /**
     * Handles click events on pill items (selection options, option groups, etc.)
     * Changes the state of the pill based on its current selection state and type
     */
    const handlePillClick = useCallback(
        (
            selectedItem: Partial<TextContentElement>,
            isSelected: boolean,
            parentItem: Partial<TextContentElement>,
            multiType?: string
        ) => {
            if (!selectedItem?.id || !selectedItem.type || !parentItem?.id)
                return

            const isValid = isValidVariant(selectedItem.type)

            const newValue = calculatePillStateChange(
                multiType ?? '',
                isSelected,
                isValid
            )

            const currentNodes = useWorkspace2Store.getState()
                .contentItemViewNodes
            const parentItemFromStore = currentNodes
                .flatMap((node) => node.elements ?? [])
                .find((el) => el.id === parentItem.id) as
                | TextContentElement
                | undefined

            const updated = updateElementById(
                (parentItemFromStore ?? parentItem) as TextContentElement,
                selectedItem as TextContentElement,
                selectedItem.id,
                newValue
            )
            if (
                selectedItem.type === 'ReplacementOption' &&
                newValue !== PROPERTY_EDITOR_PILL_STATE_VARIANT.OptedIn &&
                !isSelected
            ) {
                // ReplacementOption being depilled (clicked to edit)
                updateOptimistically(
                    parentItem.id,
                    updated.elements as TextContentElement[],
                    true,
                    true,
                    selectedItem
                )
            } else if (
                selectedItem.type === 'ReplacementOption' &&
                newValue === PROPERTY_EDITOR_PILL_STATE_VARIANT.InActive &&
                isSelected
            ) {
                // ReplacementOption OptedIn → editable (clicked to edit)
                // DON'T update the store - just trigger auto-focus via textContentElementToAdd
                // The ReplacementOption component will handle showing editable UI via local 'active' state
                // This avoids the slow store update and component re-render

                // Pass the element with explicit OptedIn state so the component knows the original state
                const elementWithOriginalState = {
                    ...selectedItem,
                    optionItemState: PROPERTY_EDITOR_PILL_STATE_VARIANT.OptedIn,
                }

                // Just trigger the auto-focus - no store update needed
                setTextContentElementToAdd(elementWithOriginalState)
            } else {
                // All other cases (SelectionOption, OptionGroup, etc.)
                activateDeactivateOptionItem(updated, parentItem.id)
            }
        },
        [
            isValidVariant,
            calculatePillStateChange,
            activateDeactivateOptionItem,
            updateOptimistically,
            setTextContentElementToAdd,
        ]
    )

    /**
     * Deletes a pill (or the whole OptionGroup when `selectionOptionId` is omitted).
     *
     * - `localOnly=true`: hard-removes without a server call (cancelling a never-saved insertion).
     * - `isUnapprovedAddition` (`changeType === ADDED`, `localOnly=false`): hard-removes from the
     *   store AND fires the server mutation so the unapproved pill is dropped server-side too.
     *   The mutation is dispatched before the store update — ContentItemInputBuilder resolves
     *   parent context from the live store.
     * - Otherwise: soft-deletes by marking options with `changeType DELETED` (track-changes strikethrough).
     */
    const handleNewPillDelete = useCallback(
        (
            optionGroupId: string,
            localOnly = false,
            selectionOptionId?: string
        ) => {
            const currentNodes = useWorkspace2Store.getState()
                .contentItemViewNodes

            const paragraphNode = currentNodes.find((node) =>
                node.elements?.some((el) => el.id === optionGroupId)
            )
            if (!paragraphNode) return

            const elements = paragraphNode.elements || []
            const ogIdx = elements.findIndex((el) => el.id === optionGroupId)
            if (ogIdx === -1) return

            const optionGroup = elements[ogIdx]
            const ogElements = (optionGroup?.elements ||
                []) as TextContentElement[]

            const targetElement = selectionOptionId
                ? ogElements.find((el) => el.id === selectionOptionId)
                : (optionGroup as Partial<TextContentElement> | undefined)
            const isUnapprovedAddition =
                targetElement?.changeType?.toUpperCase() ===
                TrackChangeType.ADDED

            if (localOnly || isUnapprovedAddition) {
                // Cancelling a brand-new, never-saved pill — it was never persisted,
                // so remove it outright instead of soft-deleting. Otherwise a "deleted"
                // pill/OptionGroup ghost is left behind (Bug: Escape leaves empty pill
                // container), since nothing filters DELETED elements out of an OptionGroup
                // when track changes are hidden.
                const remainingOptions = selectionOptionId
                    ? ogElements.filter((el) => el.id !== selectionOptionId)
                    : []

                const updatedElements =
                    remainingOptions.length > 0
                        ? elements.map((el, idx) =>
                              idx === ogIdx
                                  ? {
                                        ...optionGroup,
                                        elements: remainingOptions,
                                    }
                                  : el
                          )
                        : elements.filter((_el, idx) => idx !== ogIdx)

                const updatedNodes = currentNodes.map((node) =>
                    node.id === paragraphNode.id
                        ? { ...node, elements: updatedElements }
                        : node
                )

                if (localOnly) {
                    setContentItemViewNodes(updatedNodes)
                    return
                }

                // Dispatch the mutation before the store update — ContentItemInputBuilder
                // resolves parent context by locating the OptionGroup in the store, so removing it
                // first leaves every context field as "" and the server rejects the call.
                executeWithOptimistic({
                    fallbackState: nodes,
                    asyncAction: async () =>
                        await mutateContentElements(
                            optionGroupId,
                            updatedElements as TextContentElement[],
                            true
                        ),
                    successCbFn: async () => {
                        if (showHideToggles().isTrackChangeToggleOn) {
                            await debouncedFetchTrackChanges()
                        }
                    },
                    errorCbFn: () => setContentItemViewNodes(currentNodes),
                })
                setContentItemViewNodes(updatedNodes)
                return
            }

            const deletedChangeType = TrackChangeType.DELETED as TextContentElement['changeType']
            const updatedOptions = selectionOptionId
                ? ogElements.map((el) =>
                      el.id === selectionOptionId
                          ? { ...el, changeType: deletedChangeType }
                          : el
                  )
                : ogElements.map((el) => ({
                      ...el,
                      changeType: deletedChangeType,
                  }))

            const updatedOG = { ...optionGroup, elements: updatedOptions }
            const updatedElements = elements.map((el, idx) =>
                idx === ogIdx ? updatedOG : el
            )

            const updatedNodes = currentNodes.map((node) =>
                node.id === paragraphNode.id
                    ? { ...node, elements: updatedElements }
                    : node
            )
            setContentItemViewNodes(updatedNodes)

            executeWithOptimistic({
                fallbackState: nodes,
                asyncAction: async () =>
                    await mutateContentElements(
                        optionGroupId,
                        updatedElements as TextContentElement[],
                        true
                    ),
                successCbFn: async () => {
                    if (showHideToggles().isTrackChangeToggleOn) {
                        await debouncedFetchTrackChanges()
                    }
                },
                errorCbFn: () => setContentItemViewNodes(currentNodes),
            })
        },
        [
            nodes,
            setContentItemViewNodes,
            executeWithOptimistic,
            mutateContentElements,
            debouncedFetchTrackChanges,
        ]
    )

    return {
        activateDeactivateOptionItem,
        handlePillClick,
        handleNewPillDelete,
        register,
        resetPillSelection: resetPillSelectionState,
    }
}
